import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "May 11, 2026";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Disclaimer | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "Marketing results disclaimer for Colorado Web Impressions. No guarantees on rankings, traffic, or revenue — and an honest explanation of why.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Disclaimer | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/disclaimer` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Disclaimer", path: "/disclaimer" },
      ]),
    ],
  }),
  component: DisclaimerPage,
});

function DisclaimerPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="Disclaimer"
        description={`Last updated: ${LAST_UPDATED}. Honest expectations about marketing results, case studies, and the information on this site.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <p>
              This site is operated by Heidlebaugh Digital LLC (dba Colorado Web
              Impressions; &ldquo;we,&rdquo; &ldquo;us,&rdquo;
              &ldquo;our&rdquo;), 3210 E Woodmen Rd Ste 110, Colorado Springs,
              CO 80920.
            </p>

            <h2>No guaranteed results</h2>
            <p>
              Search engine rankings, traffic, lead volume, and revenue depend
              on many factors outside our control — including search engine
              algorithms, competitor activity, your local market, your offer,
              your pricing, and how quickly you respond to leads. We do not
              guarantee specific rankings, traffic numbers, conversion rates,
              or revenue outcomes. Anyone who does is not telling the truth.
            </p>
            <p>
              What we <em>do</em> guarantee is professional, in-house
              workmanship and that we will do exactly what is in your signed
              scope of work.
            </p>

            <h2>Case studies and testimonials</h2>
            <p>
              Case studies, screenshots, and testimonials on this site reflect
              real results for real clients. They are illustrative, not
              predictive. Your results will depend on your specific business,
              market, and engagement scope.
            </p>

            <h2>Informational content</h2>
            <p>
              Articles on our{" "}
              <Link to="/learn" className="font-bold text-brand-gold-deep underline">
                Learn (Blog)
              </Link>{" "}
              and{" "}
              <Link to="/answers" className="font-bold text-brand-gold-deep underline">
                Answers
              </Link>{" "}
              sections are provided for general information only. They are not
              legal, financial, tax, or accounting advice. Always consult a
              qualified professional for advice specific to your situation.
            </p>

            <h2>External links</h2>
            <p>
              Our site links to external resources for context and citations.
              We do not control those sites and are not responsible for their
              content, accuracy, or privacy practices.
            </p>

            <h2>No affiliate compensation</h2>
            <p>
              When we name a tool, vendor, or platform on this site, we do not
              earn a commission. If that ever changes, we will disclose it
              inline. See our{" "}
              <Link
                to="/editorial-policy"
                className="font-bold text-brand-gold-deep underline"
              >
                editorial policy
              </Link>
              .
            </p>

            <h2>Contact</h2>
            <p>
              Questions? Email{" "}
              <a
                href="mailto:info@coloradowebimpressions.com"
                className="font-bold text-brand-gold-deep underline"
               aria-label="Email Colorado Web Impressions">
                info@coloradowebimpressions.com
              </a>{" "}
              or call{" "}
              <a href="tel:+17194193935" className="font-bold text-brand-gold-deep underline" aria-label="Call (719) 419-3935">
                (719) 419-3935
              </a>
              .
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
